import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TeacketContext from 'src/context/TeacketContext';
import { useNavigate } from 'react-router-dom';
import useTheme from 'src/hooks/useTheme';
import { Space, Popover, Tooltip, Checkbox } from 'antd';
import TicketListWithGrouping from 'src/components/pages/Teacket/Components/TicketListWithGrouping';
import { getTickets2 } from 'src/api/teacket';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import SkeletonTable from 'src/components/layout/SkeletonTable';
import Highlighter from 'react-highlight-words';
import UserImage from 'src/components/layout/UserImage';
import BarLoader from 'react-bar-loader';
import TicketStatusColor from '../../Components/TicketStatusColor';
import TicketPriorityColor from '../../Components/TicketPriorityColor';
import { FaTableColumns } from 'react-icons/fa6';
import TicketTypeTag from '../../Components/TicketTypeTag';
import CmdBtn from 'src/components/layout/CmdBtn';
import TicketDrawer from '../../Components/TicketDrawer';
import { isRequestCancelled, localDownload } from 'src/misc/Misc';
import TicketStage from '../../Components/TicketStage';
import useAsyncProcesses from 'src/hooks/useAsyncProcesses';
import StyledButton from 'src/components/layout/StyledButton';
import WhenDesktop from 'src/components/layout/WhenDesktop';
import Space2 from 'src/components/layout/Space2';
import FuncKeys from 'src/components/misc/FuncKeys';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const TicketListControl2 = ({ allowSorting = false, getColumnsSelector }) => {
  const { theme } = useTheme();
  const context = useContext(TeacketContext);
  const { ticketList2selectedFilters, ticketList2LoadData, setTicketList2LoadData, ticketList2Refresh, setTicketList2Refresh, ticketList2Export, setTicketList2Export, visibleColumns } = context;
  const { executeAsyncProcess } = useAsyncProcesses();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [ticketListQuerying, setTicketListQuerying] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [pageSize] = useState(parseInt(25));

  const { ticketListSorter, ticketListQuery } = useContext(TeacketContext);

  useEffect(() => {
    if (getColumnsSelector)
      getColumnsSelector(
        <Tooltip title='Show/Hide columns'>
          <Popover content={columnsContent} title='Show/Hide Columns' trigger='click'>
            <StyledButton color={theme.textBase} size='small'>
              <Space2>
                <FaTableColumns />
                <WhenDesktop>Columns</WhenDesktop>
              </Space2>
            </StyledButton>
          </Popover>
        </Tooltip>
      );
  }, [visibleColumns, theme]);

  useEffect(() => {
    if (!ticketList2LoadData) return;
    setTicketList2LoadData(false);
    loadData();
  }, [ticketList2LoadData]);

  useEffect(() => {
    if (!ticketList2Refresh) return;
    setTicketList2Refresh(false);
    loadData();
  }, [ticketList2Refresh]);

  useEffect(() => {
    if (!ticketList2Export) return;
    setTicketList2Export(false);
    exportData();
  }, [ticketList2Export]);

  useEffect(() => {
    console.log('ticketListSorter', ticketListSorter);
    if (!ticketListSorter) return;
    loadData();
  }, [ticketListSorter]);

  useEffect(() => {
    console.log('=== ticketListQuery:', ticketListQuery);
    loadData();
  }, [ticketListQuery]);

  const loadData = async () => {
    console.log('loadData', ticketList2selectedFilters);
    try {
      setIsLoading(true);
      setTicketListQuerying(true);
      setTickets([]);

      const resp = await getTickets2(currentPage, pageSize, ticketListSorter?.field ?? null, ticketListSorter?.order ?? null, ticketListQuery, ticketList2selectedFilters);

      setTickets(resp?.entities ?? []);
      setTicketsTotal(resp?.total ?? 0);
    } catch (error) {
      if (true === isRequestCancelled(error)) return;
      console.error(error);
    } finally {
      setTicketListQuerying(false);
      setIsLoading(false);
    }
  };

  const exportData = () => {
    console.log('exportData()', ticketList2selectedFilters);
    const separator = ',';
    executeAsyncProcess(async () => {
      try {
        const resp = await getTickets2(1, 1000, ticketListSorter?.field ?? null, ticketListSorter?.order ?? null, ticketListQuery, ticketList2selectedFilters);
        let tickets = JSON.parse(JSON.stringify(resp?.entities));
        console.log('Tickets received from backend:', tickets);

        tickets.forEach((item) => {
          if (!item.wrap_up_code) {
            item.wrap_up_code = '';
          }
          if (item.owner_id) {
            item.owner = item?.members?.find((x) => x.user_id === item.owner_id)?.name ?? '';
          }

          item.members = item.members.map((m) => `${m.name} (${m.role})`).join('; ');
        });

        const replacer = (key, value) => (value === null ? '' : value); // specify how you want to handle null values here
        const header = Object.keys(tickets[0]);

        let csv = tickets.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(separator));
        csv.unshift(header.join(separator));
        csv = csv.join('\r\n');

        localDownload(csv, 'export.csv');
      } catch (error) {
        console.error(error);
      }
    });
  };

  const columns = [
    {
      title: 'Id',
      showInGroupBy: false,
      key: 'id',
      dataIndex: 'id',
      width: 140,
      sorter: true,
      render: (text, record) => (
        <Space>
          <CmdBtn
            type='link'
            style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={async () => {
              context.setTicketListSelectedTicket(text);
            }}
            onClickCommandShiftPressed={() => {
              navigate(`/teacket/tickets/${record.id}`);
            }}
            onClickCommandPressed={() => {
              window.open(`/teacket/tickets/${record.id}`, '_blank');
            }}>
            <strong>{record.id}</strong>
          </CmdBtn>
          {record?.type && <TicketTypeTag type={record.type} styling='-ml-4 -mr-4' />}
        </Space>
      ),
    },
    {
      title: 'Title',
      key: 'title',
      dataIndex: 'title',
      width: 420,
      sorter: true,
      render: (text) => (
        <div style={{ maxWidth: '400px', wordWrap: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Highlighter textToHighlight={text ?? ''} searchWords={[`${context.ticketListQuery ?? ''}`]} highlightStyle={theme.highlightStyle} unhighlightStyle={null} autoEscape={true} />
        </div>
      ),
    },
    {
      title: 'Account',
      showInGroupBy: true,
      key: 'account',
      dataIndex: 'acc_name',
      sorter: true,
      render: (text) => (
        <div>
          <Highlighter textToHighlight={text ?? ''} searchWords={[`${context.ticketListQuery ?? ''}`]} highlightStyle={theme.highlightStyle} unhighlightStyle={null} autoEscape={true} />
        </div>
      ),
    },
    {
      title: 'Members',
      key: 'members',
      dataIndex: 'members',
      align: 'left',
      width: 220,
      showInGroupBy: false,
      render: (text) => {
        return (
          <div className='flex flex-col gap-1 m-2 flex-wrap'>
            {text?.map((x, i) => {
              return (
                <div key={i} className='flex flex-row gap-2 items-center'>
                  <UserImage image={x?.user_pic} size='25px' />
                  <div style={{ lineHeight: '0.5rem' }}>
                    <div className='text-sm'>{x?.name}</div>
                    <div className='text-[0.6rem] font-light opacity-60'>{x?.role}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: 'Category',
      showInGroupBy: true,
      key: 'category',
      dataIndex: 'category',
      align: 'center',
      sorter: true,
      render: (text, record) => (
        <div style={{ wordWrap: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Highlighter textToHighlight={text ?? ''} searchWords={[`${context.ticketListQuery ?? ''}`]} highlightStyle={theme.highlightStyle} unhighlightStyle={null} autoEscape={true} />
          <div className='text-xs font-light opacity-60'>
            <Highlighter textToHighlight={record?.sub_category ?? ''} searchWords={[`${context.ticketListQuery ?? ''}`]} highlightStyle={theme.highlightStyle} unhighlightStyle={null} autoEscape={true} />
          </div>
        </div>
      ),
    },
    {
      title: 'Priority',
      showInGroupBy: true,
      key: 'priority',
      dataIndex: 'priority',
      align: 'center',
      sorter: true,
      render: (text, record) => (
        <div className='flex flex-row gap-2 items-center justify-center'>
          <TicketPriorityColor serviceId={record?.service_id} priority={text} />
          {text}
        </div>
      ),
    },
    {
      title: 'Status',
      showInGroupBy: true,
      key: 'status',
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (text, record) => {
        return (
          <>
            <div className='flex flex-row gap-2 items-center justify-center'>
              <TicketStatusColor serviceId={record?.service_id} status={text} />
              {text}
            </div>
            <TicketStage serviceId={record?.service_id} status={text} />
          </>
        );
      },
    },
    {
      title: 'Created',
      showInGroupBy: false,
      key: 'created_at',
      dataIndex: 'created_at',
      align: 'center',
      sorter: true,
      render: (text) =>
        text ? (
          <>
            <div>{dayjs(text).fromNow()}</div>
            <div className='font-thin text-xs'>at {dayjs(text).format('LLL')}</div>
          </>
        ) : (
          <>n/a</>
        ),
    },
    {
      title: 'Closed',
      showInGroupBy: false,
      key: 'closed_at',
      dataIndex: 'closed_at',
      align: 'center',
      sorter: true,
      render: (text) =>
        text ? (
          <>
            <div>{dayjs(text).fromNow()}</div>
            <div className='font-thin text-xs'>at {dayjs(text).format('LLL')}</div>
          </>
        ) : (
          <>n/a</>
        ),
    },
    {
      title: 'Need Completed By',
      showInGroupBy: false,
      key: 'need_completed_by',
      dataIndex: 'need_completed_by',
      align: 'center',
      sorter: true,
      render: (text) =>
        text ? (
          <>
            <div>{dayjs(text).fromNow()}</div>
            <div className='font-thin text-xs'>at {dayjs(text).format('LLL')}</div>
          </>
        ) : (
          <>n/a</>
        ),
    },
    {
      title: 'Forecasted',
      showInGroupBy: false,
      key: 'forecasted_date',
      dataIndex: 'forecasted_date',
      align: 'center',
      sorter: true,
      render: (text) =>
        text ? (
          <>
            <div>{dayjs(text).fromNow()}</div>
            <div className='font-thin text-xs'>at {dayjs(text).format('LLL')}</div>
          </>
        ) : (
          <>n/a</>
        ),
    },
  ].map((col) => ({ ...col, sorter: allowSorting ? col.sorter : null }));

  const columnsContent = (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {context.columnOrder.map((key) => {
        const column = columns.find((col) => col.key === key);
        return (
          <div key={column.key} style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox checked={context.visibleColumns[column.key]} onChange={() => context.toggleColumnVisibility(column.key)}>
              {column.title}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );

  const filteredColumns = columns.filter((col) => context.columnOrder.includes(col.key) && context.visibleColumns[col.key]).sort((a, b) => context.columnOrder.indexOf(a.key) - context.columnOrder.indexOf(b.key));

  const renderShowTotal = (total, range) => (
    <div className='inline-flex items-center text-sm dark:text-gray-100 dark:bg-gray-800 text-gray-600 bg-gray-50 px-2 py-1 rounded-md'>
      <span>Showing&nbsp;</span>
      <span>
        {range[0]}-{range[1]}
      </span>
      <span className='mx-1'>of</span>
      <span className='font-medium dark:text-blue-400 text-blue-600'>{total}</span>
      <span className='ml-1'>{total > 1 ? 'tickets' : 'ticket'}</span>
    </div>
  );

  const paginationConfig = {
    current: currentPage,
    showSizeChanger: false,
    pageSize,
    showTotal: context.ticketListGroupBy ? undefined : renderShowTotal,
    size: 'small',
    total: ticketsTotal,
    onChange: (page) => {
      setCurrentPage(page);
    },
    position: ['topRight', 'bottomRight'],
  };

  return (
    <>
      <TicketDrawer />
      <div className={`relative ${ticketListQuerying ? 'disabled' : null}`}>
        {!tickets ? (
          <SkeletonTable />
        ) : (
          <>
            {ticketListQuerying ? <BarLoader color={theme.primary} backgroundColor={theme.light} height='2' width='100%' /> : <div style={{ height: '2px' }} />}
            <FuncKeys>
              <TicketListWithGrouping loading={isLoading} pagination={paginationConfig} columns={filteredColumns} dataSource={tickets} rowKey='id' />
            </FuncKeys>
          </>
        )}
      </div>
      {/* </div> */}
    </>
  );
};

TicketListControl2.propTypes = {
  userId: PropTypes.string,
  column: PropTypes.object,
  index: PropTypes.number,
  moveColumn: PropTypes.func,
  tickets: PropTypes.array,
  showRequesterAndUserTickets: PropTypes.bool,
  accountId: PropTypes.string,
  opportunityId: PropTypes.string,
  allowSorting: PropTypes.bool,
  getColumnsSelector: PropTypes.func,
};

export default TicketListControl2;
